import { isPlainObject } from "lodash-es";

import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";
import {
  TableColumnConfigInputDisplayConfig,
  TableCurrencyCode,
} from "src/clients/case-management";
import { isISO8601 } from "src/utils/datetime";

export type CellDisplayType =
  | { type: "string" }
  | { type: "number" }
  | { type: "boolean" }
  | { type: "date" }
  | { type: "datetime" }
  | { type: "currency"; symbol: TableCurrencyCode }
  | { type: "enum" }
  | { type: "tags" }
  | { type: "list" }
  | { type: "object" };

export const formatCurrency = (value: number, currencyCode: string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(value);

export const extractCurrencyAmount = (value: unknown): number | null => {
  if (typeof value === "number") return value;
  if (isPlainObject(value)) {
    const amount = (value as Record<string, unknown>).amount;
    if (typeof amount === "number") return amount;
  }
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

export const getAllKeys = (records: TableBlockMultiColumnRecord): string[] =>
  Array.from(new Set(records.flatMap((record) => Object.keys(record))));

/** Handles circular references or other values that JSON.stringify throws on. */
export const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? "{}";
  } catch {
    return "{}";
  }
};

export const getClearedValue = (
  type: CellDisplayType["type"],
): unknown | undefined => {
  if (type === "enum") return null;
  if (type === "tags") return [];
  return undefined;
};

export const isExplicitActivationCell = (
  type: CellDisplayType["type"],
): boolean =>
  type === "list" ||
  type === "object" ||
  type === "enum" ||
  type === "tags" ||
  type === "boolean" ||
  type === "date" ||
  type === "datetime";

const isValueCompatible = (
  value: unknown,
  displayType: CellDisplayType,
): boolean => {
  switch (displayType.type) {
    case "string":
    case "enum":
      return typeof value !== "object" || value === null;
    case "number":
      return (
        typeof value === "number" ||
        (typeof value === "string" && !isNaN(Number(value)))
      );
    case "boolean":
      return typeof value === "boolean";
    case "date":
    case "datetime":
      return typeof value === "string";
    case "currency":
      return extractCurrencyAmount(value) !== null;
    case "tags":
      return Array.isArray(value) || typeof value !== "object";
    case "list":
    case "object":
      return true;
  }
};

export const resolveEffectiveType = (
  value: unknown,
  configuredType: CellDisplayType,
): CellDisplayType => {
  if (value === null || value === undefined) return configuredType;
  if (isValueCompatible(value, configuredType)) return configuredType;
  return resolveColumnType(null, value);
};

export const resolveColumnType = (
  displayConfig: TableColumnConfigInputDisplayConfig | null | undefined,
  value: unknown,
): CellDisplayType => {
  if (displayConfig != null && displayConfig.type != null) {
    switch (displayConfig.type) {
      case "currency":
        return {
          type: "currency",
          symbol: displayConfig.symbol,
        };
      case "boolean":
      case "date":
      case "datetime":
      case "enum":
      case "list":
      case "number":
      case "object":
      case "string":
      case "tags":
        return { type: displayConfig.type };
    }
  }

  if (value === null || value === undefined) {
    return { type: "string" };
  }

  if (typeof value === "boolean") {
    return { type: "boolean" };
  }

  if (typeof value === "number") {
    return { type: "number" };
  }

  if (Array.isArray(value)) {
    return { type: "list" };
  }

  if (isPlainObject(value)) {
    return { type: "object" };
  }

  if (typeof value === "string" && isISO8601(value)) {
    return { type: "datetime" };
  }

  return { type: "string" };
};
